import { LocationIcon } from '@/components/icons/LocationIcon'
import { WalletIcon } from '@/components/icons/WalletIcon'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { Tooltip } from 'radix-ui'
import { useEffect, useRef, useState } from 'react'

const SIZES = {
  sm: { wrapper: 'h-6 w-6 md:mb-1', icon: '14px' },
  md: { wrapper: 'h-8 w-8', icon: '18px' }
} as const

interface IAvailabilityIconsProps {
  available: string[]
  size: keyof typeof SIZES
}

export function AvailabilityIcons({
  available,
  size
}: IAvailabilityIconsProps) {
  const t = useTranslations('Services')
  const { wrapper, icon } = SIZES[size]
  const containerRef = useRef<HTMLDivElement>(null)
  const [openItem, setOpenItem] = useState<string | null>(null)
  const justToggledByTouchRef = useRef(false)

  useEffect(() => {
    if (!openItem) return

    // Touch has no hover-out, so close on the next tap outside this row.
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpenItem(null)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [openItem])

  return (
    <Tooltip.Provider delayDuration={200}>
      <div ref={containerRef} className="contents">
        {available.map((item) => (
          <Tooltip.Root
            key={item}
            open={openItem === item}
            onOpenChange={(next) => setOpenItem(next ? item : null)}
          >
            <Tooltip.Trigger asChild>
              <span
                className={cn(
                  'flex items-center justify-center rounded-md bg-white',
                  wrapper
                )}
                onPointerDown={(event) => {
                  if (event.pointerType !== 'touch') return
                  event.preventDefault()
                  justToggledByTouchRef.current = true
                  setOpenItem((current) => (current === item ? null : item))
                }}
                onClick={(event) => {
                  // Swallow the synthetic click Radix uses to auto-close.
                  if (!justToggledByTouchRef.current) return
                  justToggledByTouchRef.current = false
                  event.preventDefault()
                }}
              >
                {item === 'branches' && (
                  <LocationIcon className="text-primary-500" size={icon} />
                )}
                {item === 'wallet' && (
                  <WalletIcon className="text-primary-500" size={icon} />
                )}
              </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="tooltip-content text-body-3 text-primary-600 rounded-md bg-white px-3 py-1.5 shadow-md"
                sideOffset={6}
              >
                {t(`categories.available.${item}`)}
                <Tooltip.Arrow className="fill-white" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        ))}
      </div>
    </Tooltip.Provider>
  )
}
